export type ReviewableContentCard = {
  id: string;
  title: string;
  section: string;
  category: string;
  last_reviewed_at?: string | null;
  is_active?: boolean | null;
};

export type ContentReviewStatus = "missing" | "due" | "due_soon" | "fresh";

export type ContentHealthCard = ReviewableContentCard & {
  dueDate: string | null;
  daysUntilDue: number | null;
  reviewStatus: ContentReviewStatus;
};

export type ContentHealthSummary = {
  totalCards: number;
  activeCards: number;
  dueNowCount: number;
  dueSoonCount: number;
  missingReviewDateCount: number;
  reviewedThisYearCount: number;
  dueCards: ContentHealthCard[];
  dueSoonCards: ContentHealthCard[];
  nextUpCards: ContentHealthCard[];
  highlightCards: ContentHealthCard[];
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const CONTENT_REVIEW_INTERVAL_DAYS = 365;
export const CONTENT_DUE_SOON_WINDOW_DAYS = 30;

const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate());

const parseReviewDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfDay(parsed);
};

const addDays = (value: Date, days: number) => new Date(value.getTime() + days * MS_PER_DAY);

const toIsoDate = (value: Date) => value.toISOString().slice(0, 10);

const compareHealthCards = (left: ContentHealthCard, right: ContentHealthCard) => {
  if (left.reviewStatus === "missing" && right.reviewStatus !== "missing") return -1;
  if (left.reviewStatus !== "missing" && right.reviewStatus === "missing") return 1;

  if (left.daysUntilDue !== right.daysUntilDue) {
    return (left.daysUntilDue ?? Number.MAX_SAFE_INTEGER) - (right.daysUntilDue ?? Number.MAX_SAFE_INTEGER);
  }

  return left.title.localeCompare(right.title);
};

export const getContentHealthSummary = (
  cards: ReviewableContentCard[],
  now = new Date()
): ContentHealthSummary => {
  const today = startOfDay(now);
  const activeCards = cards.filter((card) => card.is_active !== false);

  const healthCards = activeCards.map<ContentHealthCard>((card) => {
    const reviewedAt = parseReviewDate(card.last_reviewed_at);

    if (!reviewedAt) {
      return {
        ...card,
        dueDate: null,
        daysUntilDue: null,
        reviewStatus: "missing",
      };
    }

    const dueAt = addDays(reviewedAt, CONTENT_REVIEW_INTERVAL_DAYS);
    const daysUntilDue = Math.round((dueAt.getTime() - today.getTime()) / MS_PER_DAY);

    let reviewStatus: ContentReviewStatus = "fresh";
    if (daysUntilDue <= 0) {
      reviewStatus = "due";
    } else if (daysUntilDue <= CONTENT_DUE_SOON_WINDOW_DAYS) {
      reviewStatus = "due_soon";
    }

    return {
      ...card,
      dueDate: toIsoDate(dueAt),
      daysUntilDue,
      reviewStatus,
    };
  });

  const dueCards = healthCards
    .filter((card) => card.reviewStatus === "missing" || card.reviewStatus === "due")
    .sort(compareHealthCards);
  const dueSoonCards = healthCards
    .filter((card) => card.reviewStatus === "due_soon")
    .sort(compareHealthCards);
  const nextUpCards = healthCards
    .filter((card) => card.reviewStatus === "fresh" || card.reviewStatus === "due_soon")
    .sort(compareHealthCards);

  return {
    totalCards: cards.length,
    activeCards: activeCards.length,
    dueNowCount: dueCards.length,
    dueSoonCount: dueSoonCards.length,
    missingReviewDateCount: healthCards.filter((card) => card.reviewStatus === "missing").length,
    reviewedThisYearCount: healthCards.filter((card) => card.reviewStatus === "fresh").length,
    dueCards,
    dueSoonCards,
    nextUpCards,
    highlightCards:
      dueCards.slice(0, 5).length > 0
        ? dueCards.slice(0, 5)
        : dueSoonCards.slice(0, 5).length > 0
          ? dueSoonCards.slice(0, 5)
          : nextUpCards.slice(0, 5),
  };
};

export const formatContentHealthDate = (value?: string | null) => {
  if (!value) return "No review date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

export const describeContentHealthCard = (card: ContentHealthCard) => {
  if (card.reviewStatus === "missing") {
    return "No review date recorded";
  }

  if (card.reviewStatus === "due") {
    if (card.daysUntilDue === 0) {
      return "Due today";
    }

    return `Due since ${formatContentHealthDate(card.dueDate)}`;
  }

  if (card.reviewStatus === "due_soon") {
    if (card.daysUntilDue === 1) {
      return "Due tomorrow";
    }

    return `Due ${formatContentHealthDate(card.dueDate)}`;
  }

  return `Next due ${formatContentHealthDate(card.dueDate)}`;
};
